<template>
    <div class="flex flex-col gap-8 w-full">
        <div
            class="grid grid-cols-2 gap-4"
            v-if="currentGameTypeSettings.teamPlayAllowed !== false && !matchStore.isRemotePlay"
        >
            <DiagonalCutButton
                cutPosition="right-top"
                @click="setPlayMode('singles')"
                :variant="currentPlayMode == 'singles' ? 'primary' : 'secondary'"
                data-testid="playmode-singles"
            >
                <span class="flex items-center gap-4">
                    {{ $t('player_selector.solo') }}
                    <svg
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                        class="h-5 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M16 12V16L14 17V13H2V17L0 16V12L1 11H15L16 12ZM11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8C9.65685 8 11 6.65685 11 5ZM13 5C13 7.76142 10.7614 10 8 10C5.23858 10 3 7.76142 3 5C3 2.23858 5.23858 0 8 0C10.7614 0 13 2.23858 13 5Z"
                            fill="white"
                        />
                    </svg>
                </span>
            </DiagonalCutButton>
            <DiagonalCutButton
                @click="setPlayMode('teams')"
                :variant="currentPlayMode == 'teams' ? 'primary' : 'secondary'"
                data-testid="playmode-teams"
            >
                <span class="flex items-center gap-4">
                    {{ $t('player_selector.teams') }}
                    <svg
                        width="19"
                        height="18"
                        viewBox="0 0 19 18"
                        fill="none"
                        class="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M14 13V17L12 18V14H2V18L0 17V13L1 12H13L14 13ZM9 7C9 5.89543 8.10457 5 7 5C5.89543 5 5 5.89543 5 7C5 8.10457 5.89543 9 7 9C8.10457 9 9 8.10457 9 7ZM11 7C11 9.20914 9.20914 11 7 11C4.79086 11 3 9.20914 3 7C3 4.79086 4.79086 3 7 3C9.20914 3 11 4.79086 11 7Z"
                            fill="white"
                        />
                        <path
                            d="M19 10V14L17 15V11H12V9H18L19 10ZM12 0C14.2091 0 16 1.79086 16 4C16 6.20914 14.2091 8 12 8V6C13.1046 6 14 5.10457 14 4C14 2.89543 13.1046 2 12 2V0Z"
                            fill="white"
                        />
                    </svg>
                </span>
            </DiagonalCutButton>
        </div>
        <BotSelection
            v-if="
                currentGameTypeSettings.botsAllowed &&
                !matchStore.isRemotePlay &&
                !matchStore.hasBotProfile &&
                !maxPlayersReached
            "
        />
        <div class="flex flex-col items-stretch gap-3 md:max-w-122">
            <PlayerCard
                v-for="matchProfile in currentPlayMode == 'teams'
                    ? unassignedPlayers
                    : sortedProfiles"
                :key="matchProfile.id"
                :player="matchProfile"
                :current-user-id="authStore.profile.id"
                :disabled="isUpdating"
                horizontal
                :badge="playerBadge(matchProfile)"
                :badge-variant="playerBadgeVariant(matchProfile)"
                :status-text="playerStatusText(matchProfile)"
                :status="playerStatus(matchProfile)"
                @remove="removeMatchProfile"
            />
            <PlayerAddCard
                v-for="slot in openSlotCount"
                :key="`invite-slot-${slot}`"
                horizontal
                data-testid="add-player-button"
                @click="$router.replace({ hash: '#player-selection' })"
                >{{ $t('player_selector.invite_player_slot') }}</PlayerAddCard
            >
        </div>
        <!-- Team assignment: vertical sections, Team 1 above Team 2 -->
        <div v-if="currentPlayMode == 'teams'" class="flex flex-col gap-3 md:max-w-122">
            <div class="text-center text-lg font-bold uppercase">{{ $t('team_assignment.team_1') }}</div>
            <template v-for="index in 2" :key="`team1-${index}`">
                <PlayerCard
                    v-if="team1Players[index - 1]"
                    :player="team1Players[index - 1]"
                    :current-user-id="authStore.profile.id"
                    :disabled="isUpdating"
                    horizontal
                    :badge="playerBadge(team1Players[index - 1])"
                    :badge-variant="playerBadgeVariant(team1Players[index - 1])"
                    :status-text="playerStatusText(team1Players[index - 1])"
                    :status="playerStatus(team1Players[index - 1])"
                    @remove="handleRemovePlayerFromTeam"
                />
                <PlayerAddCard horizontal @click="showPlayerSelectionModal(1)" v-else>
                    {{ $t('player_selector.assign_player') }}
                </PlayerAddCard>
            </template>

            <div class="pt-3 text-center text-lg font-bold uppercase">{{ $t('team_assignment.team_2') }}</div>
            <template v-for="index in 2" :key="`team2-${index}`">
                <PlayerCard
                    v-if="team2Players[index - 1]"
                    :player="team2Players[index - 1]"
                    :current-user-id="authStore.profile.id"
                    :disabled="isUpdating"
                    horizontal
                    :badge="playerBadge(team2Players[index - 1])"
                    :badge-variant="playerBadgeVariant(team2Players[index - 1])"
                    :status-text="playerStatusText(team2Players[index - 1])"
                    :status="playerStatus(team2Players[index - 1])"
                    @remove="handleRemovePlayerFromTeam"
                />
                <PlayerAddCard horizontal @click="showPlayerSelectionModal(2)" v-else>
                    {{ $t('player_selector.assign_player') }}
                </PlayerAddCard>
            </template>
        </div>
    </div>
    <TeamAssignmentModal
        :is-visible="showTeamModal"
        :mode="modalMode"
        :player="selectedPlayerForTeam"
        :selected-team-number="selectedTeamForPlayers"
        :available-players="unassignedPlayers"
        :team1-count="team1Players.length"
        :team2-count="team2Players.length"
        :is-assigning="isUpdating"
        @assign-team="handleTeamAssignment"
        @assign-player="handlePlayerAssignment"
        @close="closeTeamModal"
    />

    <!-- Mode Change Confirmation Modal -->
    <div
        v-if="showModeChangeConfirmation"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        @click.self="cancelModeChange"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-6 text-center">
                    <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                        {{ $t('player_selector.switch_singles_title') }}
                    </h2>
                    <p class="text-sm text-gray-400">
                        {{ $t('player_selector.switch_singles_body') }}
                    </p>
                </div>

                <div class="mb-4 text-center">
                    <p class="text-sm text-red-400">
                        {{ assignedPlayersCount > 1
                            ? $t('player_selector.assigned_count_plural', { n: assignedPlayersCount })
                            : $t('player_selector.assigned_count_singular', { n: assignedPlayersCount }) }}
                    </p>
                </div>
            </div>

            <div class="border-stroke-1 mt-4 grid grid-cols-2 gap-4 border-t p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    @click="cancelModeChange"
                >
                    {{ $t('general.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    @click="confirmModeChange"
                >
                    {{ $t('player_selector.switch_singles_confirm') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>

    <PlayerSelectionDrawer :maxPlayersReached="maxPlayersReached" />
</template>

<script>
import PlayerSelectionDrawer from './PlayerSelectionDrawer.vue'
import { useMatchStore } from '@/stores/match.js'
import PlayerCard from '@/components/players/PlayerCard.vue'
import { useAuthStore } from '@/stores/auth.js'
import PlayerAddCard from '@/components/players/PlayerAddCard.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import { useFriends } from '@/composables/useFriends.js'
import { useTeamManagement } from '@/composables/useTeamManagement.js'
import { usePlayerValidation } from '@/composables/usePlayerValidation.js'
import TeamAssignmentModal from '@/components/TeamAssignmentModal.vue'
import BotSelection from './BotSelection.vue'

export default {
    name: 'PlayerSelectionSection',
    components: {
        PlayerSelectionDrawer,
        PlayerCard,
        PlayerAddCard,
        DiagonalCutButton,
        TeamAssignmentModal,
        BotSelection,
    },
    setup() {
        const friendsComposable = useFriends()
        const teamManagement = useTeamManagement()
        const playerValidation = usePlayerValidation(teamManagement)

        return {
            ...friendsComposable,
            ...teamManagement,
            ...playerValidation,
        }
    },
    data() {
        return {
            matchStore: useMatchStore(),
            authStore: useAuthStore(),
        }
    },
    computed: {
        sortedProfiles() {
            const profiles = this.matchStore.setup.profiles || []
            const creatorProfileId = this.matchStore.setup.creator_profile_id

            if (!creatorProfileId) return profiles

            return [...profiles].sort((a, b) => {
                const aIsCreator = a.profile_id === creatorProfileId
                const bIsCreator = b.profile_id === creatorProfileId

                if (aIsCreator) return -1
                if (bIsCreator) return 1
                return 0
            })
        },
        maxTeamSize() {
            const totalPlayers = (this.matchStore.setup.profiles || []).length
            return Math.ceil(totalPlayers / 2)
        },
        currentPlayMode() {
            return this.playMode || 'singles'
        },
        /**
         * One "Invite player ..." row per open spot, up to the effective cap
         * (see usePlayerValidation's effectiveMaxPlayers: solo limit locally in
         * singles mode or online, the game type's absolute maximum in local
         * teams mode).
         */
        openSlotCount() {
            const count = (this.matchStore.setup.profiles || []).length
            return Math.max(0, this.effectiveMaxPlayers - count)
        },
    },
    mounted() {
        this.initializeTeams()
    },
    methods: {
        playerBadge(matchProfile) {
            if (matchProfile.profile_id === this.matchStore.setup.creator_profile_id) {
                return this.$t('player_selector.host_badge')
            }
            return matchProfile.ready
                ? this.$t('player_selector.joined_badge')
                : this.$t('player_selector.invite_sent_badge')
        },
        playerBadgeVariant(matchProfile) {
            const isHost =
                matchProfile.profile_id === this.matchStore.setup.creator_profile_id
            return isHost || matchProfile.ready ? 'success' : 'neutral'
        },
        playerStatus(matchProfile) {
            if (matchProfile.profile_id === this.matchStore.setup.creator_profile_id) return 'host'
            return matchProfile.ready ? 'joined' : 'invited'
        },
        playerStatusText(matchProfile) {
            return this.matchStore.isBotProfile(matchProfile.profile_id ?? matchProfile.id)
                ? this.$t('player_selector.bot_status')
                : ''
        },
        async removeMatchProfile(id) {
            try {
                await this.matchStore.removeProfileFromMatch(id)
            } catch (error) {
                console.error('Failed to remove profile:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.message || this.$t('player_selector.error_remove_player'),
                )
            }
        },
        async setPlayMode(mode) {
            if (mode !== 'teams' && !this.canSwitchToSinglesMode) {
                this.$toast(
                    this.$t('player_selector.too_many_players_title'),
                    5000,
                    this.getSinglesModeBlockedMessage(),
                )
                return
            }

            if (mode === 'singles' && this.playMode === 'teams' && this.assignedPlayersCount > 0) {
                this.openModeChangeConfirmation(mode)
                return
            }

            await this.performModeChange(mode)
        },
        async performModeChange(mode) {
            try {
                await this.switchPlayMode(mode)
                this.hideValidation()
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.message || this.$t('player_selector.error_set_play_mode'),
                )
            }
        },
        showTeamAssignmentModal(player) {
            this.openTeamAssignmentModal(player)
        },
        showPlayerSelectionModal(teamNumber) {
            this.openPlayerSelectionModal(teamNumber, this.unassignedPlayers)
        },
        async handleTeamAssignment({ player, teamNumber }) {
            const teamUUID = this.getTeamUUID(teamNumber)
            if (teamUUID) {
                try {
                    await this.assignPlayerToTeam(player, teamUUID)
                    this.hideValidation()
                } catch (error) {
                    this.$toast(
                        this.$t('general.something_went_wrong'),
                        5000,
                        error.message || this.$t('player_selector.error_assign_player'),
                    )
                }
            }
            this.closeTeamModal()
        },
        async handlePlayerAssignment({ player, teamNumber }) {
            const teamUUID = this.getTeamUUID(teamNumber)
            if (teamUUID) {
                try {
                    await this.assignPlayerToTeam(player, teamUUID)
                    this.hideValidation()
                } catch (error) {
                    this.$toast(
                        this.$t('general.something_went_wrong'),
                        5000,
                        error.message || this.$t('player_selector.error_assign_player'),
                    )
                }
            }
            this.closeTeamModal()
        },
        async handleRemovePlayerFromTeam(playerOrId) {
            try {
                const player =
                    typeof playerOrId === 'object'
                        ? playerOrId
                        : this.matchStore.setup.profiles.find(
                              (p) => p.id === playerOrId || p.profile_id === playerOrId,
                          )

                if (!player) {
                    throw new Error(this.$t('match_errors.player_not_found'))
                }

                await this.removePlayerFromTeam(player)
                this.hideValidation()
            } catch (error) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.message || this.$t('player_selector.error_remove_from_team'),
                )
            }
        },
        async confirmModeChange() {
            if (this.pendingModeChange) {
                await this.performModeChange(this.pendingModeChange)
            }
            this.closeModeChangeConfirmation()
        },
        cancelModeChange() {
            this.closeModeChangeConfirmation()
        },
    },
}
</script>

<style>
.avatarBG {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    /* background: linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, transparent 50%); */
    background: linear-gradient(
        135.09deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.035) 20%,
        transparent 40%
    );
    pointer-events: none;
}
</style>
